import { isSpanContextValid, SpanKind, type Attributes, type Counter } from "@opentelemetry/api";

import {
  EcwChecksumError,
  EcwDeadlineExceededError,
  EcwFarmTriggerError,
  EcwHttpStatusError,
  EcwOperationFailedError,
  EcwPreDispatchError,
  EcwResponseParseError,
  EcwSessionConfigurationError,
  EcwSessionCryptoError,
  EcwSessionDeadError,
  EcwSessionMiss,
  EcwSessionNotReadyError,
  EcwSessionStoreError,
} from "@/src/lib/ehr/ecw/webecw/errors";
import {
  BadRequestError,
  ConflictError,
  MissingMrnError,
  NotFoundError,
  UnauthorizedError,
  UnprocessableEntityError,
} from "@/src/lib/error";
import { createLogger } from "@/src/lib/logging/logger";
import { getApplicationMeter } from "@/src/lib/telemetry/application-metrics";
import { classifyHttpTransportError } from "@/src/lib/telemetry/http-error";
import { createOperationMetrics } from "@/src/lib/telemetry/operation-metrics";
import { runInSpan, setSpanAttributes } from "@/src/lib/telemetry/tracing";

const logger = createLogger({ fileName: "webecw-observability" });
const SERVICE_NAME = "webecw-metrics";
const METRIC_VERSION = "v3";

export enum WebecwOperationKind {
  READ = "read",
  WRITE = "write",
}

export enum WebecwErrorType {
  CHECKSUM = "checksum",
  CLIENT_ERROR = "client_error",
  DEADLINE_EXCEEDED = "deadline_exceeded",
  ERROR = "error",
  FARM_TRIGGER = "farm_trigger",
  HTTP_STATUS = "http_status",
  NETWORK_ERROR = "network_error",
  OPERATION_FAILED = "operation_failed",
  RESPONSE_PARSE = "response_parse",
  RATE_LIMITED = "rate_limited",
  REQUEST_TIMEOUT = "request_timeout",
  SESSION_CRYPTO = "session_crypto",
  SESSION_CONFIGURATION = "session_configuration",
  SESSION_DEAD = "session_dead",
  SESSION_NOT_READY = "session_not_ready",
  SESSION_STORE = "session_store",
  UNKNOWN_ERROR = "unknown_error",
}

export interface WebecwErrorDetails {
  errorType: WebecwErrorType;
  httpStatus?: number;
  parseReason?: string;
  responseBodyKind?: string;
  sessionReason?: string;
}

export interface WebecwErrorLogDetails extends WebecwErrorDetails {
  ecwPath?: string;
  parseStage?: string;
  responseBytes?: number;
  responseShape?: EcwResponseParseError["responseShape"];
  validationIssues?: EcwResponseParseError["validationIssues"];
}

export interface WebecwOperationContext {
  groupId: string;
  kind: WebecwOperationKind;
  operation: string;
  source?: string;
}

interface WebecwHttpRequestContext {
  credentialSlug: string;
  groupId: string;
  method: string;
  path: string;
}

let fallbacks: Counter | undefined;

const operationMetrics = createOperationMetrics({
  meter: () => getApplicationMeter(SERVICE_NAME),
  completed: {
    name: "webecw_operations_total",
    description: "Completed logical WebECW operations by outcome and bounded error classification",
  },
  duration: {
    name: "webecw_operation_duration_ms",
    description: "Logical WebECW operation latency",
    unit: "ms",
  },
  onRecordError: logMetricError,
});

const httpRequestMetrics = createOperationMetrics({
  meter: () => getApplicationMeter(SERVICE_NAME),
  completed: {
    name: "webecw_http_requests_total",
    description: "Completed WebECW HTTP requests by normalized path, outcome, and response classification",
  },
  duration: {
    name: "webecw_http_request_duration_ms",
    description: "WebECW HTTP request latency",
    unit: "ms",
  },
  onRecordError: logMetricError,
});

export function classifyWebecwError(error: unknown): WebecwErrorDetails {
  if (error instanceof EcwPreDispatchError) return classifyWebecwError(error.cause);

  if (
    error instanceof BadRequestError ||
    error instanceof ConflictError ||
    error instanceof MissingMrnError ||
    error instanceof NotFoundError ||
    error instanceof UnauthorizedError ||
    error instanceof UnprocessableEntityError
  ) {
    return { errorType: WebecwErrorType.CLIENT_ERROR };
  }

  if (error instanceof EcwHttpStatusError) {
    return {
      errorType: error.status === 429 ? WebecwErrorType.RATE_LIMITED : WebecwErrorType.HTTP_STATUS,
      httpStatus: error.status,
      ...(error.responseBodyKind ? { responseBodyKind: error.responseBodyKind } : {}),
    };
  }

  if (error instanceof EcwDeadlineExceededError) {
    return { errorType: WebecwErrorType.DEADLINE_EXCEEDED };
  }

  const transportError = classifyHttpTransportError(error);

  if (transportError === "timeout") {
    return { errorType: WebecwErrorType.REQUEST_TIMEOUT };
  }

  if (transportError === "network_error") {
    return { errorType: WebecwErrorType.NETWORK_ERROR };
  }

  if (error instanceof EcwSessionNotReadyError) {
    return {
      errorType: error.miss === EcwSessionMiss.DEAD ? WebecwErrorType.SESSION_DEAD : WebecwErrorType.SESSION_NOT_READY,
      ...(error.reason ? { sessionReason: error.reason } : {}),
    };
  }

  if (error instanceof EcwSessionDeadError) {
    return { errorType: WebecwErrorType.SESSION_DEAD, sessionReason: error.reason };
  }

  if (error instanceof EcwSessionConfigurationError) {
    return { errorType: WebecwErrorType.SESSION_CONFIGURATION, sessionReason: error.reason };
  }

  if (error instanceof EcwResponseParseError) {
    return {
      errorType: WebecwErrorType.RESPONSE_PARSE,
      httpStatus: error.status,
      parseReason: error.parseReason,
      ...(error.responseBodyKind ? { responseBodyKind: error.responseBodyKind } : {}),
    };
  }

  if (error instanceof EcwOperationFailedError) return { errorType: WebecwErrorType.OPERATION_FAILED };
  if (error instanceof EcwChecksumError) return { errorType: WebecwErrorType.CHECKSUM };
  if (error instanceof EcwSessionCryptoError) return { errorType: WebecwErrorType.SESSION_CRYPTO };
  if (error instanceof EcwSessionStoreError) return { errorType: WebecwErrorType.SESSION_STORE };
  if (error instanceof EcwFarmTriggerError) {
    return {
      errorType: WebecwErrorType.FARM_TRIGGER,
      ...(error.status === null ? {} : { httpStatus: error.status }),
    };
  }

  if (error instanceof Error) return { errorType: WebecwErrorType.ERROR };

  return { errorType: WebecwErrorType.UNKNOWN_ERROR };
}

export function describeWebecwErrorForLogging(error: unknown): WebecwErrorLogDetails {
  const unwrapped = error instanceof EcwPreDispatchError ? error.cause : error;
  const details = classifyWebecwError(unwrapped);

  if (!(unwrapped instanceof EcwResponseParseError)) return details;

  return {
    ...details,
    ecwPath: unwrapped.path,
    parseStage: unwrapped.parseStage,
    responseBytes: unwrapped.responseBytes,
    responseShape: unwrapped.responseShape,
    validationIssues: unwrapped.validationIssues,
  };
}

export function recordWebecwFallback(
  context: WebecwOperationContext,
  trigger: unknown,
  reason: string,
  outcome: "error" | "success"
): void {
  recordFallbackSafely((counter) => {
    counter.add(1, {
      ...errorAttributes(trigger),
      fallback_outcome: outcome,
      group_id: context.groupId,
      kind: context.kind,
      operation: context.operation,
      reason,
      source: context.source ?? "unspecified",
      version: METRIC_VERSION,
    });
  });
}

function errorAttributes(error: unknown): Attributes {
  const details = classifyWebecwError(error);

  return {
    error_type: details.errorType,
    ...(details.httpStatus === undefined ? {} : { http_status: String(details.httpStatus) }),
    ...(details.parseReason ? { parse_reason: details.parseReason } : {}),
    ...(details.responseBodyKind ? { response_body_kind: details.responseBodyKind } : {}),
    ...(details.sessionReason ? { session_reason: details.sessionReason } : {}),
  };
}

function logMetricError(error: unknown): void {
  logger.warn("WebECW metric recording failed; request outcome is unchanged", { error });
}

function recordFallbackSafely(record: (counter: Counter) => void): void {
  try {
    fallbacks ??= getApplicationMeter(SERVICE_NAME).createCounter("webecw_fallbacks_total", {
      description: "Completed Cobalt fallbacks after a WebECW error, by trigger and fallback outcome",
    });

    record(fallbacks);
  } catch (error) {
    logMetricError(error);
  }
}

export async function withWebecwOperationMetrics<T>(context: WebecwOperationContext, fn: () => Promise<T>): Promise<T> {
  const baseAttributes: Attributes = {
    group_id: context.groupId,
    kind: context.kind,
    operation: context.operation,
    source: context.source ?? "unspecified",
    version: METRIC_VERSION,
  };

  try {
    return await operationMetrics.run(baseAttributes, fn, errorAttributes);
  } catch (error) {
    logger.warn("WebECW operation failed", { ...context, ...classifyWebecwError(error) });

    throw error;
  }
}

export async function withWebecwHttpRequestMetrics<T>(
  context: WebecwHttpRequestContext,
  fn: () => Promise<T>
): Promise<T> {
  const baseAttributes: Attributes = {
    credential_slug: context.credentialSlug,
    group_id: context.groupId,
    method: context.method,
    path: context.path,
    version: METRIC_VERSION,
  };

  return runInSpan(
    "webecw.http_request",
    async (span) => {
      try {
        const result = await httpRequestMetrics.run(baseAttributes, fn, errorAttributes);

        setSpanAttributes(span, {
          "ehr.outcome": "success",
          "http.response.status_code": 200,
        });

        return result;
      } catch (error) {
        const details = classifyWebecwError(error);

        setSpanAttributes(span, {
          "ehr.outcome": "error",
          ...(details.httpStatus === undefined ? {} : { "http.response.status_code": details.httpStatus }),
          "webecw.error_type": details.errorType,
          ...(details.parseReason ? { "webecw.parse_reason": details.parseReason } : {}),
          ...(details.responseBodyKind ? { "webecw.response_body_kind": details.responseBodyKind } : {}),
        });

        if (error instanceof EcwResponseParseError) {
          const spanContext = span?.spanContext();

          logger.warn("WebECW response failed validation", {
            credentialSlug: context.credentialSlug,
            groupId: context.groupId,
            method: context.method,
            ...describeWebecwErrorForLogging(error),
            ...(spanContext && isSpanContextValid(spanContext)
              ? { spanId: spanContext.spanId, traceId: spanContext.traceId }
              : {}),
          });
        }

        throw error;
      }
    },
    {
      kind: SpanKind.CLIENT,
      attributes: {
        "ehr.backend": "webecw",
        "ehr.operation": "http_request",
        "http.request.method": context.method,
        "http.route": context.path,
      },
    }
  );
}
